// src/lib/queryClient.ts
//
// SPA-wide react-query client, provided at src/index.js and shared by every
// surface that uses react-query (currently the admin-v2 operator console).
// Decoupled from any one feature's API layer: the auth-error short-circuit is
// duck-typed on `error.status`, so it works for any thrown error that carries
// an HTTP status (e.g. admin-v2's ApiError) without importing its class.

import { QueryClient } from '@tanstack/react-query';

const isAuthError = (error: unknown): boolean => {
  const status = (error as { status?: number } | null)?.status;
  return status === 401 || status === 403;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 60-second stale time matches the console's polling interval.
      staleTime: 60 * 1000,

      // Keep data in cache for 5 minutes after unmount.
      gcTime: 5 * 60 * 1000,

      // Don't retry auth errors. Retry others once after 1s.
      retry: (failureCount, error) => {
        if (isAuthError(error)) return false;
        return failureCount < 1;
      },
      retryDelay: 1000,

      // We control refresh cadence via polling, not window focus.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      retry: (failureCount, error) => {
        if (isAuthError(error)) return false;
        return failureCount < 1;
      },
    },
  },
});
